import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { fetchNeedsYou } from "@/services/needsYou";
import type { NeedsYouResponse } from "@/types/needsYou";

export const NEEDS_YOU_KEY_ROOT = "needs_you";

export function needsYouKey(appId: string | null) {
  return [NEEDS_YOU_KEY_ROOT, appId] as const;
}

export function needsYouUpdatesKey(appId: string | null) {
  return [NEEDS_YOU_KEY_ROOT, appId, "updates"] as const;
}

/**
 * The read is a shell probe (the bell mounts it on every authenticated
 * route), so it keeps the ENG-APP-UX-TUNEUP floor's 60 s for status probes
 * (row 11) rather than refetching on every navigation.
 */
export const NEEDS_YOU_STALE_MS = 60_000;

/**
 * The ONE Needs-you list for the selected app (§ 12 § 5 item 3): the
 * Dashboard, Active and the bell read this one query, so they show the same
 * list, never copies. The query carries the abort signal so a superseded
 * fetch is cancelled on the wire, not only in the cache.
 */
export function useNeedsYou(appId: string | null) {
  return useQuery<NeedsYouResponse, Error>({
    queryKey: needsYouKey(appId),
    queryFn: ({ signal }) => {
      if (!appId) throw new Error("appId required");
      return fetchNeedsYou(appId, { signal });
    },
    enabled: Boolean(appId),
    staleTime: NEEDS_YOU_STALE_MS,
  });
}

/**
 * The bell's Updates news lines (`?include=updates`): a separate read keyed
 * apart from the list, made only while the Updates tab is open — the plain
 * shell read never pays the outbox scan behind it.
 */
export function useNeedsYouUpdates(appId: string | null, enabled: boolean) {
  return useQuery<NeedsYouResponse, Error>({
    queryKey: needsYouUpdatesKey(appId),
    queryFn: ({ signal }) => {
      if (!appId) throw new Error("appId required");
      return fetchNeedsYou(appId, { includeUpdates: true, signal });
    },
    enabled: Boolean(appId) && enabled,
    staleTime: NEEDS_YOU_STALE_MS,
  });
}

/**
 * Freshness after a decision: every item on the list is cleared by a
 * mutation (start or dismiss a bet, review a result, apply a winner,
 * reconnect the console, add a competitor, fill the app context), so ONE
 * MutationCache subscription — mounted once, by the shell's bell — refetches
 * the list whenever any mutation succeeds, and the next item opens on
 * whichever surface the decision was taken. `cancelRefetch: false` lets a
 * second invalidation join the fetch already in flight instead of
 * restarting it.
 */
export function useNeedsYouRefetchOnMutation(appId: string | null) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!appId) return undefined;
    return queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "success") return;
      void queryClient.invalidateQueries(
        { queryKey: needsYouKey(appId) },
        { cancelRefetch: false },
      );
    });
  }, [appId, queryClient]);
}
